import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Flow } from '../obj/Flow';
import { StorageRouterService } from '../services/storage-router.service';
import { FlowManagementService } from '../services/flow-management.service';
import { ApiService } from '../services/api.service';
import { MsalHelperService } from '../services/msal-helper.service';

type FlowViewTab = 'view' | 'runs' | 'snapshots' | 'json' | 'diagram';
const FLOW_VIEW_TABS: FlowViewTab[] = ['view', 'runs', 'snapshots', 'json', 'diagram'];

@Component({
  selector: 'app-flow-view',
  templateUrl: './flow-view.component.html',
  styleUrls: ['./flow-view.component.scss'],
  standalone: false
})
export class FlowViewComponent implements OnInit, OnDestroy {
  public flow: Flow | null = null;
  public activeTab: FlowViewTab = 'view';
  public error = '';
  public savingRules = false;
  public admin = false;
  public deep = false;
  public status = '';
  /** Flips the JSON tab into editorType="FlowAdvanced" — toggled via
   *  ?advanced=true on `/flows/:env/:name/json`. */
  public advanced = false;
  public rulesForm: UntypedFormGroup;

  private subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private storageService: StorageRouterService,
    private flowManagementService: FlowManagementService,
    private apiService: ApiService,
    private msalHelperService: MsalHelperService,
    private formBuilder: UntypedFormBuilder
  ) {
    this.rulesForm = this.formBuilder.group({
      monitor: new FormControl(false),
      notifyOnFail: new FormControl(false),
      notifyOnMissed: new FormControl(false),
      missingDays: new FormControl(0),
      email: new FormControl('')
    });
  }

  async ngOnInit() {
    // Canonicalize legacy `/view?tab=runs` URLs to the per-tab path (`/runs`).
    // Run before the data/queryParam subscriptions so we don't briefly render
    // the wrong tab during the redirect.
    const legacyTab = this.route.snapshot.queryParamMap.get('tab') || '';
    const pathTab = this.route.snapshot.data?.['tab'];
    if (
      legacyTab &&
      FLOW_VIEW_TABS.includes(legacyTab as FlowViewTab) &&
      pathTab !== legacyTab
    ) {
      const env = this.route.snapshot.paramMap.get('envName') || '';
      const flowName = this.route.snapshot.paramMap.get('flowName') || '';
      if (env && flowName) {
        const remaining: { [k: string]: string } = {};
        for (const k of this.route.snapshot.queryParamMap.keys) {
          if (k === 'tab') continue;
          const v = this.route.snapshot.queryParamMap.get(k);
          if (v != null) remaining[k] = v;
        }
        await this.router.navigate(
          ['/flows', env, flowName, legacyTab],
          { queryParams: remaining, replaceUrl: true }
        );
        return;
      }
    }

    // Active tab comes from the route config now (e.g. `/runs` → 'runs').
    // `?tab=` stays as a fallback for the rare case the route data doesn't
    // carry a tab (e.g. /view fallback path).
    this.subscription.add(
      this.route.data.subscribe((data) => {
        const fromPath = `${data?.['tab'] || ''}` as FlowViewTab;
        if (FLOW_VIEW_TABS.includes(fromPath)) {
          this.activeTab = fromPath;
        }
      })
    );

    this.subscription.add(
      this.route.queryParamMap.subscribe((params) => {
        if (!FLOW_VIEW_TABS.includes(this.route.snapshot.data?.['tab'])) {
          const tab = `${params.get('tab') || 'view'}` as FlowViewTab;
          this.activeTab = FLOW_VIEW_TABS.includes(tab) ? tab : 'view';
        }
        this.admin = `${params.get('admin') || ''}`.toLowerCase() === 'true';
        this.deep = `${params.get('deep') || ''}`.toLowerCase() === 'true';
        this.status = params.get('status') || '';
        this.advanced = `${params.get('advanced') || ''}`.toLowerCase() === 'true';
      })
    );

    const envName = this.route.snapshot.paramMap.get('envName') || '';
    const flowName = this.route.snapshot.paramMap.get('flowName') || '';
    if (!envName || !flowName) {
      this.error = 'Missing flow route parameters.';
      return;
    }

    this.flowManagementService.currentEnvironment = envName;

    const cached = [
      ...await this.storageService.getFlows(),
      ...await this.storageService.getFlowsAdmin()
    ].find((flow: Flow) => flow.environment === envName && flow.flowName === flowName);

    this.flow = cached || Object.assign(new Flow(), {
      environment: envName,
      flowName,
      displayName: flowName,
      flowState: 'Started',
      runs: []
    });

    this.loadRulesForm();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public selectTab(tab: FlowViewTab) {
    if (!this.flow) return;
    // Navigate to the per-tab path. `queryParamsHandling: 'preserve'` keeps
    // `admin=true`, `deep=true`, and `status=…` across tab switches.
    this.router.navigate(
      ['/flows', this.flow.environment, this.flow.flowName, tab],
      { queryParamsHandling: 'preserve', replaceUrl: true }
    );
  }

  /** True when the current flow has at least one resolvable connection ref. */
  public get hasConnections(): boolean {
    const ref = (this.flow as any)?.connections;
    if (Array.isArray(ref)) return ref.length > 0;
    if (ref && typeof ref === 'object') return Object.keys(ref).length > 0;
    return false;
  }

  public get connectionRefs(): { key: string; api: string; displayName: string }[] {
    const flow: any = this.flow;
    if (!flow) return [];
    const refs = flow.connections;
    if (Array.isArray(refs)) {
      return refs.map((r) => ({
        key: r?.connectionName || r?.name || '',
        api: r?.api?.name || r?.apiName || '',
        displayName: r?.displayName || r?.api?.properties?.displayName || r?.api?.name || ''
      }));
    }
    if (refs && typeof refs === 'object') {
      return Object.entries(refs).map(([key, value]: [string, any]) => ({
        key,
        api: value?.api?.name || value?.apiName || '',
        displayName: value?.displayName || value?.api?.properties?.displayName || key
      }));
    }
    return [];
  }

  public backToFlows() {
    this.router.navigate(['/flows']);
  }

  public async saveRules() {
    if (!this.flow || this.rulesForm.invalid) {
      return;
    }

    this.savingRules = true;
    this.error = '';
    const value = this.rulesForm.value;
    const key = `${this.flow.environment}.${this.flow.flowName}`;

    try {
      await this.apiService.mutateFlow(key, {
        monitor: value.monitor,
        rule_notify_onfail: !!value.notifyOnFail,
        rule_notify_onmissingdays: value.notifyOnMissed ? Number(value.missingDays || 0) : 0,
        rule_notify_email: value.email
      }, () => {});

      Object.assign(this.flow, {
        monitor: value.monitor,
        rule_notify_onfail: !!value.notifyOnFail,
        rule_notify_onmissingdays: value.notifyOnMissed ? Number(value.missingDays || 0) : 0,
        rule_notify_email: value.email
      });
    } catch (err: any) {
      this.error = err?.message || 'Failed to save flow rules.';
    } finally {
      this.savingRules = false;
    }
  }

  private loadRulesForm() {
    if (!this.flow) {
      return;
    }

    const currentUser = this.msalHelperService.getUser();
    this.rulesForm.setValue({
      monitor: !!(this.flow as any).monitor,
      notifyOnFail: !!(this.flow as any).rule_notify_onfail,
      notifyOnMissed: Number((this.flow as any).rule_notify_onmissingdays || 0) > 0,
      missingDays: Number((this.flow as any).rule_notify_onmissingdays || 0),
      email: (this.flow as any).rule_notify_email || currentUser?.username || ''
    });

    const email = this.rulesForm.get('email');
    const updateEmailValidation = () => {
      const required = this.rulesForm.get('notifyOnFail')?.value || this.rulesForm.get('notifyOnMissed')?.value;
      if (required) {
        email?.setValidators([Validators.required]);
      } else {
        email?.clearValidators();
      }
      email?.updateValueAndValidity();
    };

    this.subscription.add(this.rulesForm.get('notifyOnFail')!.valueChanges.subscribe(updateEmailValidation));
    this.subscription.add(this.rulesForm.get('notifyOnMissed')!.valueChanges.subscribe(updateEmailValidation));
    updateEmailValidation();
  }
}
